// Orkestrator E1 §2 — pure parallel entitlements. No server, DB or `.env` imports (same split as
// `byoa-guard.ts`): unit tests load it without env; `parallel.server.ts` resolves the user's plan
// and wraps the result. `-1` means unlimited everywhere in this module.
import type { PlanLimits } from '#/lib/types'
import { basePlanSlug } from '#/lib/ai/models'

export interface ParallelLimits {
  /** cloud swarm: sessions of one workflow that may run at once */
  parallelRuns: number
  /** `bystep serve` (local executor): sessions of one workflow that may run at once */
  serveParallel: number
  /** reviewer (agent) sessions per period — consumed by E2, resolved here so every tier reads from one table */
  reviewerRuns: number
}
export const UNLIMITED = -1
/** Per-tier defaults by base plan slug (CONTRACT-E1 §2). Explicit `PlanLimits.*` values win. */
/**
 * Lead decision (E1 gate, 10 Sep 2026): `parallelRuns`/`serveParallel` default to 4 for EVERY plan —
 * exactly what `WorkflowInput` (1..4) allowed before E1, so nothing is reduced ("tidak ada yang berkurang").
 * The 402 `parallel_runs` gate only bites when `pricing_plan.limits.parallelRuns/serveParallel` is set
 * explicitly lower in the DB (owner decision in E6/K2; proposal table in PLAN_EKSEKUSI_ORKESTRATOR.md §12).
 * `reviewerRuns` is a new feature and keeps the contract values.
 */
export const PARALLEL_DEFAULTS: Record<string, ParallelLimits> = {
  free: { parallelRuns: 4, serveParallel: 4, reviewerRuns: 3 },
  starter: { parallelRuns: 4, serveParallel: 4, reviewerRuns: 20 },
  builder: { parallelRuns: 4, serveParallel: 4, reviewerRuns: 20 },
  pro: { parallelRuns: 4, serveParallel: 4, reviewerRuns: 60 },
  fable: { parallelRuns: 4, serveParallel: 4, reviewerRuns: 120 },
  studio: { parallelRuns: 4, serveParallel: 4, reviewerRuns: 300 },
  campus: { parallelRuns: 4, serveParallel: 4, reviewerRuns: 20 },
  facilitator: { parallelRuns: 4, serveParallel: 4, reviewerRuns: 20 },
}
export const ADMIN_PARALLEL: ParallelLimits = { parallelRuns: UNLIMITED, serveParallel: UNLIMITED, reviewerRuns: UNLIMITED }

const limitValue = (value: unknown): number | null => typeof value === 'number' && Number.isInteger(value) && (value === UNLIMITED || value >= 1) ? value : null
/** Env override name for `parallelRuns` of a tier: `PARALLEL_<SLUG>_RUNS` (base slug, upper-case, `-` → `_`). */
export const parallelEnvKey = (slug: string) => `PARALLEL_${basePlanSlug(slug).toUpperCase().replace(/[^A-Z0-9]+/g, '_')}_RUNS`

/**
 * Precedence per field: `PlanLimits` override → env `PARALLEL_<SLUG>_RUNS` (parallelRuns only) →
 * defaults via the base slug (`pro-usd` / `pro-12mo` → `pro`; unknown → free).
 */
export function resolveParallelLimits(planLimits: Partial<PlanLimits> | null | undefined, slug: string, env: Record<string, string | undefined> = process.env): ParallelLimits {
  const defaults = PARALLEL_DEFAULTS[basePlanSlug(slug)] ?? PARALLEL_DEFAULTS.free!
  const fromEnv = limitValue(env[parallelEnvKey(slug)] === undefined ? undefined : Number(env[parallelEnvKey(slug)]))
  return {
    parallelRuns: limitValue(planLimits?.parallelRuns) ?? fromEnv ?? defaults.parallelRuns,
    serveParallel: limitValue(planLimits?.serveParallel) ?? defaults.serveParallel,
    reviewerRuns: limitValue(planLimits?.reviewerRuns) ?? defaults.reviewerRuns,
  }
}

export type ParallelAdmission = { ok: true; maxParallel: number; requested: number } | { ok: false; code: 'parallel_runs'; limit: number; requested: number }
/**
 * `enqueueWorkflow` admission (CONTRACT-E1 §2): an explicit request above the entitlement is refused
 * (402, never silently trimmed); machine/cloud capacity trims silently. `requested` defaults to
 * `min(2, entitlement)` so a workflow started without a number behaves exactly like before E1.
 */
export function admitParallel(input: { requested?: number | null; entitlement: number; capacity: number | null }): ParallelAdmission {
  const unlimited = input.entitlement === UNLIMITED
  const requested = input.requested ?? (unlimited ? 2 : Math.min(2, input.entitlement))
  if (!unlimited && requested > input.entitlement) return { ok: false, code: 'parallel_runs', limit: input.entitlement, requested }
  // `capacity: null` = the executor never declared one (legacy daemon without `--parallel`): nothing trims.
  const capacity = input.capacity === null || !Number.isFinite(input.capacity) ? requested : Math.max(1, Math.floor(input.capacity) || 1)
  return { ok: true, maxParallel: Math.max(1, Math.min(requested, unlimited ? requested : input.entitlement, capacity)), requested }
}

/** UTC day window for `workspace.daily_usd_cap` accounting. */
export const utcDayStart = (now = new Date()) => new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))

// ---------------------------------------------------------------- Orkestrator E6 §1: top-ups (`addon_credit`)
/** +N concurrent agents (both executors) while `expires_at` is in the future — `parallel` top-up (30 days). */
export const PARALLEL_BOOST_KIND = 'parallel_boost'
/** Pemeriksa sessions spent after the monthly plan quota — `reviewer` top-up (no expiry). */
export const REVIEWER_RUNS_KIND = 'reviewer_runs'

export interface AddonCreditLike { kind: string; remaining: number; expiresAt: Date | null }
const liveCredit = (r: AddonCreditLike, now: Date) => r.remaining > 0 && (!r.expiresAt || r.expiresAt > now)
/** Sum of active boosts: purchases stack, an expired (or admin-zeroed) row adds nothing. */
export const activeParallelBoost = (rows: AddonCreditLike[], now = new Date()) => rows.filter(r => r.kind === PARALLEL_BOOST_KIND && liveCredit(r, now)).reduce((a, r) => a + r.remaining, 0)
/** Unspent reviewer top-up sessions. */
export const reviewerAddonRemaining = (rows: AddonCreditLike[], now = new Date()) => rows.filter(r => r.kind === REVIEWER_RUNS_KIND && liveCredit(r, now)).reduce((a, r) => a + r.remaining, 0)

/**
 * Plan entitlement + top-ups. Without top-ups the result is the plan entitlement unchanged.
 * - `parallelRuns`/`serveParallel` + active boost (−1 stays −1).
 * - `reviewerRuns` becomes this month's allowance: `max(used, plan) + unspent top-up` — top-up sessions are
 *   spent only after the plan quota (`spendsReviewerAddon`), so `used >= allowance` still means "used up".
 */
export function applyParallelAddons(plan: ParallelLimits, addons: { parallelBoost: number; reviewerAddon: number; reviewerUsed: number }): ParallelLimits {
  const whole = (n: number) => Math.max(0, Math.floor(Number.isFinite(n) ? n : 0))
  const boost = whole(addons.parallelBoost), reviewerAddon = whole(addons.reviewerAddon), used = whole(addons.reviewerUsed)
  const boosted = (n: number) => (n === UNLIMITED ? UNLIMITED : n + boost)
  return {
    parallelRuns: boosted(plan.parallelRuns),
    serveParallel: boosted(plan.serveParallel),
    reviewerRuns: plan.reviewerRuns === UNLIMITED || reviewerAddon === 0 ? plan.reviewerRuns : Math.max(used, plan.reviewerRuns) + reviewerAddon,
  }
}
/** After a Pemeriksa session raised the month's counter to `usedAfter`, is it paid by a top-up? */
export const spendsReviewerAddon = (usedAfter: number, planReviewerRuns: number) => planReviewerRuns !== UNLIMITED && usedAfter > planReviewerRuns
